import "./index.scss";

import { FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { ReactComponent as IconHelp } from "assets/images/icons/help.svg";
import { ReactComponent as IconSupport } from "assets/images/square-buttons/support-green.svg";
import { ReactComponent as IconSeparator } from "assets/images/ui/dash-separator.svg";
import Dropdown from "components/Dropdown";
import Input from "components/Input";
import _ from "lodash";
import { memo, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import HeartRateZoneWizardModal from "../HeartRateZoneWizardModal";
import ProgramsExercisesGrid from "../ProgramsExercisesGrid";

const WorkoutPlanCardio = ({ perceivedExertionOptions, cardioWizardProtocols, memberDob }) => {
  const formContext = useFormContext();
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = formContext;
  const [showHeartRateZoneWizardModal, setShowHeartRateZoneWizardModal] = useState(false);
  const [optionsPerceivedExertion, setOptionsPerceivedExertion] =
    useState(perceivedExertionOptions);
  const [overallGoalSelected, setOverallGoalSelected] = useState(
    getValues("cardio_overall_goal.goal")
  );
  const watchCardioSessionsPerWeek = watch("cardio_program_frequency");
  const watchOverallGoalDailyMin = watch("cardio_overall_goal.daily_min_value");
  const watchOverallGoalDailyMax = watch("cardio_overall_goal.daily_max_value");
  const watchPrograms = watch("programs");

  const [programs, setPrograms] = useState();

  useEffect(() => {
    setValue(
      "cardio_overall_goal.weekly_min_value",
      watchOverallGoalDailyMin * watchCardioSessionsPerWeek
    );
  }, [setValue, watchCardioSessionsPerWeek, watchOverallGoalDailyMin]);

  useEffect(() => {
    setValue(
      "cardio_overall_goal.weekly_max_value",
      watchOverallGoalDailyMax * watchCardioSessionsPerWeek
    );
  }, [setValue, watchCardioSessionsPerWeek, watchOverallGoalDailyMax]);

  //#region event handlers
  const handlePrimaryGoalToggle = (value: string) => {
    setOverallGoalSelected(value);
    //setValue("cardio_overall_goal.goal", value);
  };

  const handleHeartRateZoneWizardReturn = (value) => {
    if (value) {
      setValue("heart_rate_min", value.heartRateBpmMin);
      setValue("heart_rate_max", value.heartRateBpmMax);
    }
    setShowHeartRateZoneWizardModal(false);
  };
  //#endregion event handlers

  return (
    <>
      <Stack className="workoutPlanCardio">
        <h2 className="headerSpacing">Define Overall Goals</h2>
        <Grid container className="primary-details-section" spacing={"34px"}>
          <Grid item xs={6}>
            <Box className="inner-container" mb="30px">
              <p className="section-title">Frequency</p>
              <Stack direction={"row"} alignContent="center" gap="40px" pl={"10px"}>
                <Box className="fieldLabel" pt={"14px"}>
                  Sessions per week
                </Box>
                <Controller
                  name="cardio_program_frequency"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Stack spacing={1}>
                      <Input
                        value={value}
                        onChange={onChange}
                        type="number"
                        min="0"
                        //TODO::
                        //IF cardio program is selected, this field is required
                        hasError={
                          !_.isEmpty(errors.cardio_program_frequency) ||
                          errors.cardio_program_frequency < 1
                        }
                      />
                      {errors.cardio_program_frequency?.type === "required" && (
                        <h4 className="error">This field is required and must be 1 or greater</h4>
                      )}
                    </Stack>
                  )}
                />
              </Stack>
            </Box>
            <Box className="inner-container">
              <p className="section-title">Intensity</p>
              <Grid container alignItems="center" mb="17px">
                <Grid item xs={2}>
                  <span className="fieldLabel">Perceived Exertion</span>
                </Grid>
                <Grid item xs={10}>
                  <Controller
                    name="perceived_exertion"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <Dropdown
                        fullWidth
                        value={value}
                        onChange={(e) => setValue("perceived_exertion", e.target.value)}
                      >
                        {optionsPerceivedExertion.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Dropdown>
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <span className="fieldLabel">Heart Rate, BPM</span>
                </Grid>
                <Grid item xs={10}>
                  <Grid
                    container
                    alignItems="center"
                    my="20px"
                    onClick={() => setShowHeartRateZoneWizardModal(true)}
                  >
                    <Grid item xs={5}>
                      <Controller
                        name="heart_rate_min"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <Input
                            disabled={true}
                            value={value}
                            onChange={onChange}
                            type="number"
                            borderRightStraight={true}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={1} className="fieldLabel">
                      Min&nbsp;&nbsp;
                      <IconSeparator />
                    </Grid>
                    <Grid item xs={5}>
                      <Controller
                        name="heart_rate_max"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <Input
                            disabled={true}
                            value={value}
                            onChange={onChange}
                            type="number"
                            borderLeftStraight={true}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={1} className="fieldLabel">
                      &nbsp;&nbsp;Max
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={2}></Grid>
                <Grid
                  item
                  xs={10}
                  className="wizardLink"
                  onClick={() => setShowHeartRateZoneWizardModal(true)}
                >
                  <IconHelp />
                  Heart Rate Zone Wizard
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="inner-container">
              <p className="section-title">Overall Goal</p>
              <Box px={"10px"}>
                <Stack
                  direction="row"
                  height={"72px"}
                  alignItems={"center"}
                  justifyContent="flex-start"
                >
                  <Stack direction="row">
                    <Box className="fieldLabel" mt={"14px"} mr={"50px"}>
                      Primary Goal
                    </Box>
                    <Controller
                      name="cardio_overall_goal.goal"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { onChange, value, ref } }) => (
                        <RadioGroup
                          row
                          className="radios"
                          name="cardio_overall_goal.goal"
                          value={value}
                          onChange={(v) => {
                            handlePrimaryGoalToggle(v.target.value);
                            onChange(v);
                          }}
                        >
                          <FormControlLabel
                            value="TIME"
                            control={
                              <Radio
                                sx={{
                                  "&.Mui-checked": {
                                    color: "#33A9A0",
                                  },
                                  color: "#D6EEEC",
                                }}
                              />
                            }
                            label="Time"
                          />
                          <FormControlLabel
                            value="CALORIES"
                            control={
                              <Radio
                                sx={{
                                  "&.Mui-checked": {
                                    color: "#33A9A0",
                                  },
                                  color: "#D6EEEC",
                                }}
                              />
                            }
                            label="Calories"
                          />
                          <FormControlLabel
                            value="HEART_RATE"
                            control={
                              <Radio
                                sx={{
                                  "&.Mui-checked": {
                                    color: "#33A9A0",
                                  },
                                  color: "#D6EEEC",
                                }}
                              />
                            }
                            label="Heart Rate"
                          />
                        </RadioGroup>
                      )}
                    />
                  </Stack>
                </Stack>
                <Box className="overallGoalDescription">
                  <Stack direction="row" alignItems={"center"}>
                    <IconSupport />
                    {overallGoalSelected === "TIME" && (
                      <span>
                        Time Measure Settings: Enter a weekly or per session target range for time.
                      </span>
                    )}
                    {overallGoalSelected === "CALORIES" && (
                      <span>
                        Calorie Measure Settings: Enter a weekly or per session target range for
                        calorie expenditure.
                      </span>
                    )}
                    {overallGoalSelected === "HEART_RATE" && (
                      <span>
                        Heart Rate Measure Settings: Enter a weekly or per session range for time in
                        target heart rate zone.
                      </span>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <Grid container alignItems={"center"} className="overallGoalGrid">
                    <Grid item xs={3}>
                      {overallGoalSelected === "calories" ? (
                        <>Calories per Session</>
                      ) : (
                        <>Minutes per Session</>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Controller
                        name="cardio_overall_goal.daily_min_value"
                        control={control}
                        rules={{ required: false }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1}>
                            <Input
                              value={value}
                              onChange={onChange}
                              type="number"
                              min={0}
                              //TODO::
                              //Fix requirement of this field
                              hasError={!_.isEmpty(errors.cardio_overall_goal?.daily_min_value)}
                            />
                            {errors.cardio_overall_goal?.daily_min_value?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Grid>
                    <Grid item xs={1} textAlign="center">
                      <IconSeparator />
                    </Grid>
                    <Grid item xs={4}>
                      <Controller
                        name="cardio_overall_goal.daily_max_value"
                        control={control}
                        rules={{ required: false }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1}>
                            <Input
                              value={value}
                              onChange={onChange}
                              type="number"
                              min={0}
                              //TODO::
                              //Fix requirement of this field
                              hasError={!_.isEmpty(errors.cardio_overall_goal?.daily_max_value)}
                            />
                            {errors.cardio_overall_goal?.daily_max_value?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      {overallGoalSelected === "calories" ? (
                        <>Calories per Week</>
                      ) : (
                        <>Minutes per Week</>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Box>
                        <Controller
                          name="cardio_overall_goal.weekly_min_value"
                          control={control}
                          rules={{ required: false }}
                          render={({ field: { onChange, value, ref } }) => (
                            <Stack spacing={1}>
                              <Input value={value} disabled type="number" />
                            </Stack>
                          )}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={1} textAlign="center">
                      <IconSeparator />
                    </Grid>
                    <Grid item xs={4}>
                      <Box>
                        <Controller
                          name="cardio_overall_goal.weekly_max_value"
                          control={control}
                          rules={{ required: false }}
                          render={({ field: { onChange, value, ref } }) => (
                            <Stack spacing={1}>
                              <Input value={value} disabled type="number" />
                            </Stack>
                          )}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <ProgramsExercisesGrid programs={getValues("cardio_workouts")} />
      </Stack>
      <HeartRateZoneWizardModal
        open={showHeartRateZoneWizardModal}
        onClose={handleHeartRateZoneWizardReturn}
        cardioWizardProtocols={cardioWizardProtocols}
        memberDob={memberDob}
      />
    </>
  );
};

export default memo(WorkoutPlanCardio);
